import { useState, useEffect } from 'react';
import { useRouter } from 'next/dist/client/router';
import { motion } from 'framer-motion';
import axios from 'axios';
import BuildingObjectsPicker from './BuildingObjectsPicker';
import BuildingObjectsList from './BuildingObjectsList';
import BuildingObjectsSkeletonList from './BuildingObjectsSkeletonList';
import BuildingObjectsTable from './BuildingObjectsTable';
import Checkbox from '../Checkbox';
import Radio from '../Radio';
import Input from '../Input';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from '../../styles/modules/building/BuildingObjects.module.scss';

const BuildingObjects = ({ rooms, buildings }) => {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);
  const [chType, setChType] = useState('list');
  const [chRooms, setChRooms] = useState([]);
  const [chPrice, setChPrice] = useState(['', '']);
  const [chPriceUnit, setChPriceUnit] = useState('all');
  const [chSquare, setChSquare] = useState(['', '']);

  useEffect(() => {
    if (!router.query.id) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/buildings',
          {
            complex: router.query.id,
            flats: true,
            rooms: chRooms.filter((el) => el !== 'filter'),
            studio: chRooms.includes('studio'),
            priceFrom: chPrice[0],
            priceTo: chPrice[1],
            priceUnit: chPriceUnit,
            squareFrom: chSquare[0],
            squareTo: chSquare[1]
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setData(response.data.data);
        setTotal(response.data.totalFlats);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        setIsLoading(false);
        setIsError(true);
      }
    };
    fetchData();

    return () => controller.abort();
  }, [chType, chRooms, chPrice, chPriceUnit, chSquare]);

  const clearFilters = () => {
    setChRooms([]);
    setChPrice(['', '']);
    setChPriceUnit('all');
    setChSquare(['', '']);
  };
  const getFilters = () => {
    return {
      complex: router.query.id,
      flats: true,
      rooms: chRooms.filter((el) => el !== 'filter'),
      studio: chRooms.includes('studio'),
      priceFrom: chPrice[0],
      priceTo: chPrice[1],
      priceUnit: chPriceUnit,
      squareFrom: chSquare[0],
      squareTo: chSquare[1]
    };
  };

  return (
    <section className="block">
      <div className="fixed">
        <h3 className={styles.title}>
          Квартиры
          <BuildingObjectsPicker onChange={(newValue) => setChType(newValue)} />
        </h3>
        <div className={styles.filters}>
          <Checkbox
            title="Количество комнат"
            checkboxes={Object.entries(rooms)
              .sort((a, b) => {
                if (a[1] === 'studio') return -1;

                return a[1] - b[1];
              })
              .map((value) => ({
                text: value[1] === 'studio' ? 'студия' : value[1] + 'К',
                value: value[1]
              }))}
            onChange={(newValues) => {
              setChRooms(newValues);
            }}
          />
          <div className={styles.filtersGroup}>
            <Input
              title="Стоимость"
              inputs={[
                { placeholder: 'Цена от', mask: 'money', max: 99999999 },
                { placeholder: 'Цена до', mask: 'money', max: 99999999 }
              ]}
              suffix="₽"
              onChange={(newValues) => {
                setChPrice(newValues);
              }}
            />
            <Radio
              radios={[
                { text: 'всё', value: 'all' },
                { text: 'м²', value: 'm2' }
              ]}
              selected="all"
              onChange={(newValues) => {
                setChPriceUnit(newValues);
              }}
              style={{ marginLeft: 8, marginTop: width > 670 ? 24 : 0 }}
            />
          </div>
          <Input
            title="Общая площадь"
            inputs={[
              { placeholder: 'Площадь от', mask: 'money', max: 999 },
              { placeholder: 'Площадь до', mask: 'money', max: 999 }
            ]}
            suffix="м²"
            onChange={(newValues) => {
              setChSquare(newValues);
            }}
          />
        </div>
        {chType === 'list' ? (
          isLoading || isError ? (
            <BuildingObjectsSkeletonList />
          ) : !!data.data.length ? (
            <BuildingObjectsList flats={data.data} total={total} filters={getFilters()} />
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 1 }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '24px 0 0 0'
              }}
            >
              <h5 style={{ marginBottom: 8 }}>Нет подходящих вариантов</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить фильтры или{' '}
                <a role="button" tabIndex={0} onClick={() => clearFilters()}>
                  сбросьте их
                </a>
              </div>
            </motion.div>
          )
        ) : (
          <BuildingObjectsTable buildings={buildings} filters={getFilters()} />
        )}
      </div>
    </section>
  );
};

export default BuildingObjects;
